/**
 * Display helpers shared by the softphone, the activity feed and the call history.
 * Label rules follow docs/VOICE_CALLS_PLAN.md §5.5 ("who called whom").
 */

export const formatPhone = (e164) => {
    if (!e164) return '';
    const d = String(e164).replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return String(e164);
};

/** A typed number the server can normalise: 10+ digits, or an explicit "+" country code with 8+ digits. */
export const isDialableNumber = (raw) => {
    const str = String(raw || '').trim();
    const digits = str.replace(/\D/g, '');
    return str.startsWith('+') ? digits.length >= 8 : digits.length >= 10;
};

export const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return '';
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
};

export const personName = (p) => (p ? `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() : '');

/** "Matthew Lee's parent", "Matthew Lee (student)", "(201) 555-1234" … */
export const whomText = (card) => {
    if (!card.student && card.teacher) {
        const teacherName = personName(card.teacher);
        if (teacherName) return `${teacherName} (teacher)`;
    }
    const name = personName(card.student);
    if (!name) return formatPhone(card.to_number);
    switch (card.contact_label) {
        case 'student': return `${name} (student)`;
        case 'parent': return `${name}'s parent`;
        case 'parent_2': return `${name}'s parent (2nd)`;
        case 'other': return `${name} (other number)`;
        default: return name;
    }
};

/** " (+ Sarah, Tom)" — the extra students a call is also filed under, by first name; '' when none. */
export const alsoText = (card) => {
    const names = (card?.extra_students || []).map((s) => s.first_name || s.last_name || '').filter(Boolean);
    return names.length ? ` (+ ${names.join(', ')})` : '';
};

export const CALL_STATUS_TEXT = {
    initiated: 'Connecting',
    ringing: 'Ringing',
    in_progress: 'On call',
    completed: 'Answered',
    no_answer: 'No answer',
    busy: 'Busy',
    failed: 'Failed',
    canceled: 'Canceled',
};

export const TERMINAL_STATUSES = ['completed', 'no_answer', 'busy', 'failed', 'canceled'];
export const isTerminal = (status) => TERMINAL_STATUSES.includes(status);

/** Colour for a call outcome chip. */
export const statusColor = (status) => {
    if (status === 'completed') return 'success';
    if (status === 'in_progress' || status === 'ringing' || status === 'initiated') return 'info';
    if (status === 'failed') return 'error';
    return 'default';
};

const UNRECOVERABLE_RE = /^\[unrecoverable\](?:\s*gave up after \d+ nightly retries:)?\s*/i;

/**
 * The one place the server's error_message conventions are parsed:
 *   "recording fetch: …"            → the recording copy failed
 *   "recording fetch (wav): …"      → same step
 *   "transcription: …"              → transcription failed
 *   "[unrecoverable] gave up after N nightly retries: <one of the above>"  → automatic retries stopped
 * Returns { step: 'recording' | 'transcript' | null, unrecoverable, message } (message without the marker).
 */
export const failureInfo = (card) => {
    // The server states this structurally (failed_step / retry_exhausted / failure_message); the
    // parse below is only a fallback for a payload that predates those fields.
    if (card && 'failed_step' in card) {
        return { step: card.failed_step || null, unrecoverable: Boolean(card.retry_exhausted), message: card.failure_message || '' };
    }
    const raw = typeof card?.error_message === 'string' ? card.error_message : '';
    const unrecoverable = UNRECOVERABLE_RE.test(raw);
    const message = raw.replace(UNRECOVERABLE_RE, '').trim();
    let step = null;
    if (card?.transcript_status === 'FAILED') step = 'transcript';
    else if (/^recording fetch/i.test(message)) step = 'recording';
    else if (/^transcription/i.test(message)) step = 'transcript';
    else if (message && !card?.has_recording) step = 'recording';
    return { step, unrecoverable, message };
};

/** A failure an admin can act on (Retry): the recording copy or the transcription, not a call that never connected. */
export const cardIsRetryable = (card) => failureInfo(card).step !== null;

/** Changes only when the failure itself changes — for "unseen failure" badges that must ignore note edits. */
export const failureSignature = (card) => {
    const f = failureInfo(card);
    return f.step ? `${card.id}:${f.step}:${f.unrecoverable ? 'x' : 'r'}:${f.message}` : null;
};

/** No student and no teacher (plan §5.11). */
export const isUnassigned = (card) => (card?.student_id == null) && !card?.student && (card?.teacher_id == null) && !card?.teacher;

/**
 * A teacher's dialable number, mirroring the server's teacherE164: US / Canada (+1) only, from
 * `phone` and `country_phone_code` ("+1 United States", "+1 Canada", "+82 South Korea"…). E.164 or null.
 */
export const teacherDialNumber = (teacher) => {
    if (!teacher?.phone) return null;
    const code = (String(teacher.country_phone_code || '+1').match(/^\s*\+?(\d{1,4})/) || [])[1] || '1';
    if (code !== '1') return null;
    const raw = String(teacher.phone).trim();
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+')) return digits.length === 11 && digits.startsWith('1') ? `+${digits}` : null;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return null;
};

/**
 * The three pipeline steps shown under each activity card.
 * state: 'done' | 'active' | 'pending' | 'skipped' | 'failed'
 */
export const pipelineSteps = (card) => {
    const failure = failureInfo(card);
    const steps = [];

    // 1. call
    if (!isTerminal(card.status)) {
        steps.push({ key: 'call', label: CALL_STATUS_TEXT[card.status] || 'Calling', state: 'active' });
    } else if (card.status === 'completed') {
        steps.push({ key: 'call', label: `Answered${card.duration_seconds != null ? ` · ${formatDuration(card.duration_seconds)}` : ''}`, state: 'done' });
    } else {
        steps.push({ key: 'call', label: CALL_STATUS_TEXT[card.status] || card.status, state: card.status === 'failed' ? 'failed' : 'skipped' });
    }

    // 2. recording
    const fetchFailed = !card.has_recording && failure.step === 'recording';
    if (card.has_recording) {
        steps.push({ key: 'recording', label: 'Recording saved', state: 'done' });
    } else if (fetchFailed) {
        steps.push({ key: 'recording', label: failure.unrecoverable ? 'Recording failed (retries stopped)' : 'Recording failed', state: 'failed', detail: failure.message, unrecoverable: failure.unrecoverable });
    } else if (card.transcript_status === 'SKIPPED' || (isTerminal(card.status) && card.status !== 'completed')) {
        steps.push({ key: 'recording', label: 'No recording', state: 'skipped' });
    } else if (isTerminal(card.status)) {
        steps.push({ key: 'recording', label: 'Waiting for recording', state: 'active' });
    } else {
        steps.push({ key: 'recording', label: 'Recording', state: 'pending' });
    }

    // 3. transcript
    switch (card.transcript_status) {
        case 'COMPLETED': steps.push({ key: 'transcript', label: 'Transcript saved', state: 'done' }); break;
        case 'PROCESSING': steps.push({ key: 'transcript', label: 'Transcribing', state: 'active' }); break;
        case 'FAILED': steps.push({ key: 'transcript', label: failure.unrecoverable ? 'Transcript failed (retries stopped)' : 'Transcript failed', state: 'failed', detail: failure.message, unrecoverable: failure.unrecoverable }); break;
        case 'SKIPPED': steps.push({ key: 'transcript', label: 'No transcript', state: 'skipped' }); break;
        default: steps.push({ key: 'transcript', label: card.has_recording ? 'Transcript queued' : 'Transcript', state: card.has_recording ? 'active' : 'pending' });
    }
    return steps;
};

/**
 * May this admin open the call (details, recording, notes, attach, retry)? Mirrors the server's
 * canOpenCall: managers (VOICE_ADMIN_EMAILS) any call, permitted admins the calls they placed.
 * `me` is GET /voice/me; a list row from the server may also carry `can_open`.
 */
export const canOpenCall = (me, card) => {
    if (!me?.permitted || !card) return false;
    if (typeof card.can_open === 'boolean') return card.can_open;
    if (me.manager) return true;
    const mine = me.admin?.id;
    const owner = card.admin?.id ?? card.admin_id;
    return mine != null && owner != null && Number(owner) === Number(mine);
};

/**
 * Which call a memo came from. Prefers the id the server stamped (`memo.call_log.id`); otherwise
 * reads it from the signature line every call memo carries ("— 통화 #42 · 담당 …"), which is the
 * call's database id written by the server. null for memos that did not come from a call.
 */
export const callIdFromMemo = (memo) => {
    if (!memo) return null;
    if (memo.call_log?.id != null) return Number(memo.call_log.id);
    const text = String(memo.content || '').replace(/<[^>]*>/g, ' ');
    const m = /(?:^|\s)—\s*통화\s*#(\d+)/.exec(text) || /통화\s*#(\d+)/.exec(text);
    return m ? Number(m[1]) : null;
};

/** All the searchable text the Calls tab holds for a call: transcript, labeled turns, notes. */
const callSearchText = (call) => [
    call?.summary || '',
    call?.transcript || '',
    ...(Array.isArray(call?.transcript_segments) ? call.transcript_segments.map((s) => s?.text || '') : []),
    call?.notes || '',
].join('\n');

/** Exact-words filter for the Calls tab: does the call's text contain the typed words (case-insensitive)? */
export const callMatchesWords = (call, term) => {
    const t = String(term || '').trim().toLowerCase();
    if (!t) return true;
    return callSearchText(call).toLowerCase().includes(t);
};

/** The turns of a call that contain the words, for the exact-words results. */
export const matchingTurns = (call, term, max = 3) => {
    const t = String(term || '').trim().toLowerCase();
    if (!t || !Array.isArray(call?.transcript_segments)) return [];
    return call.transcript_segments.filter((s) => (s?.text || '').toLowerCase().includes(t)).slice(0, max);
};

/** The words of a query worth marking in a result: the whole phrase plus each word of 2+ characters. */
export const queryTerms = (q) => {
    const phrase = String(q || '').trim();
    if (!phrase) return [];
    const words = phrase.split(/\s+/).filter((w) => w.length >= 2);
    return [...new Set([phrase, ...words])];
};

/**
 * Split text into [{ text, hit }] parts so the UI can mark the matched words. `terms` is a phrase or
 * a list of phrases; every case-insensitive occurrence of any of them is a hit, overlaps merged.
 */
export const highlightParts = (text, terms) => {
    const src = String(text || '');
    const list = (Array.isArray(terms) ? terms : [terms]).map((t) => String(t || '').trim()).filter(Boolean);
    if (!list.length || !src) return [{ text: src, hit: false }];
    const lower = src.toLowerCase();
    const ranges = [];
    for (const t of list) {
        const needle = t.toLowerCase();
        let i = 0;
        for (;;) {
            const j = lower.indexOf(needle, i);
            if (j < 0) break;
            ranges.push([j, j + t.length]);
            i = j + t.length;
        }
    }
    if (!ranges.length) return [{ text: src, hit: false }];
    ranges.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const r of ranges) {
        const last = merged[merged.length - 1];
        if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]); else merged.push([...r]);
    }
    const parts = [];
    let i = 0;
    for (const [a, b] of merged) {
        if (a > i) parts.push({ text: src.slice(i, a), hit: false });
        parts.push({ text: src.slice(a, b), hit: true });
        i = b;
    }
    if (i < src.length) parts.push({ text: src.slice(i), hit: false });
    return parts;
};

/** A card that needs a human to look at it. */
export const cardHasFailure = (card) => pipelineSteps(card).some((s) => s.state === 'failed');

/** The call-state phases during which a call is in progress from the admin's point of view. */
export const BUSY_STATUSES = ['preparing', 'connecting', 'ringing', 'in_call'];
